import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  CheckCircle2,
  Clock,
  DollarSign,
  ExternalLink,
  Eye,
  LayoutGrid,
  List,
  Pencil,
  Search,
  Trash2,
  XCircle,
} from "lucide-react";

import type { BusinessListing, User as UserType } from "@/types";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { orgScopedPath } from "@/lib/orgDoor";
import { canActOnListingOrg, isOrgAdmin } from "@/lib/roles";
import { useToast } from "@/components/ui/toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import Loader from "@/components/ui/loader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import ListingHotBadge from "@/components/ListingHotBadge";
import { Pagination } from "@/components/ui/pagination";
import {
  EmptyPanel,
  MetricTile,
  PageHero,
  PagePanel,
  PageShell,
  PanelHeading,
} from "@/components/interior-page";

interface LookupItem {
  id: number;
  name: string;
}

const LISTING_STATUSES: { id: number; name: string; label: string }[] = [
  { id: 1, name: "draft", label: "Draft" },
  { id: 2, name: "under_review", label: "Under Review" },
  { id: 3, name: "active", label: "Active" },
  { id: 9, name: "loi", label: "LOI" },
  { id: 4, name: "under_offer", label: "Under Offer" },
  { id: 10, name: "due_diligence", label: "Due Diligence" },
  { id: 11, name: "escrow", label: "Escrow" },
  { id: 5, name: "sold", label: "Sold" },
  { id: 6, name: "expired", label: "Expired" },
  { id: 7, name: "withdrawn", label: "Withdrawn" },
  { id: 8, name: "rejected", label: "Rejected" },
];

const STATUS_COLORS: Record<number, string> = {
  1: "bg-slate-100 text-slate-600",
  2: "bg-amber-500/15 text-amber-600",
  3: "bg-emerald-500/15 text-emerald-600",
  4: "bg-cyan-500/15 text-cyan-600",
  5: "bg-sky-500/15 text-sky-600",
  6: "bg-zinc-100 text-zinc-700",
  7: "bg-orange-500/15 text-orange-600",
  8: "bg-rose-500/15 text-rose-600",
  9: "bg-purple-500/15 text-purple-600",
  10: "bg-indigo-500/15 text-indigo-600",
  11: "bg-teal-500/15 text-teal-600",
};

const STATUS_ICONS: Record<number, ReactNode> = {
  1: null,
  2: <Clock className="h-3.5 w-3.5" />,
  3: <CheckCircle2 className="h-3.5 w-3.5" />,
  4: <DollarSign className="h-3.5 w-3.5" />,
  5: <CheckCircle2 className="h-3.5 w-3.5" />,
  6: <XCircle className="h-3.5 w-3.5" />,
  7: <XCircle className="h-3.5 w-3.5" />,
  8: <XCircle className="h-3.5 w-3.5" />,
  9: <DollarSign className="h-3.5 w-3.5" />,
  10: <Clock className="h-3.5 w-3.5" />,
  11: <CheckCircle2 className="h-3.5 w-3.5" />,
};

const PAGE_SIZE = 21;

export default function AdminListingsPage() {
  // Needed to tell which rows this admin may actually act on: an org_admin
  // sees every listing but can only moderate their own organization's.
  const { user } = useAuth();
  // An org admin can VIEW every listing but only moderate their own org's, so
  // an unfiltered queue would be almost entirely un-actionable. Default them to
  // their own organization; the toggle still reaches everything.
  const [onlyMyOrg, setOnlyMyOrg] = useState(isOrgAdmin(user));
  const [listings, setListings] = useState<BusinessListing[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  // Page lives in the URL (?page=N, 0-based) so that returning from a listing
  // detail via "Back to listings" restores the exact page the admin was on.
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(0, Number(searchParams.get("page") ?? "0") || 0);
  const setPage = (next: number) => {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next > 0) p.set("page", String(next));
        else p.delete("page");
        return p;
      },
      { replace: true },
    );
  };
  const [searchQuery, setSearchQuery] = useState("");
  const [filterStatusId, setFilterStatusId] = useState<string>("");
  const [filterAgentId, setFilterAgentId] = useState<string>("");
  const [filterCategoryId, setFilterCategoryId] = useState<string>("");
  const [filterCountryId, setFilterCountryId] = useState<string>("");
  // Id of the default (United States) row once the lookup lands — "" if that row
  // is missing, which simply leaves the filter on "All countries".
  const defaultCountryIdRef = useRef("");
  const [countryReady, setCountryReady] = useState(false);
  const [filterStateId, setFilterStateId] = useState<string>("");
  const [filterCityId, setFilterCityId] = useState<string>("");
  const [agents, setAgents] = useState<UserType[]>([]);
  const [categories, setCategories] = useState<LookupItem[]>([]);
  const [countries, setCountries] = useState<LookupItem[]>([]);
  const [states, setStates] = useState<LookupItem[]>([]);
  const [cities, setCities] = useState<LookupItem[]>([]);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [, setStatusDropdown] = useState<string | null>(null);
  const [notesModal, setNotesModal] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [listingSeenAt, setListingSeenAt] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<"list" | "table">("list");
  const toast = useToast();

  useEffect(() => {
    api.get<UserType[]>("/admin/advisors").then(setAgents).catch(console.error);
    // Retired categories included: their listings still need filtering.
    api.get<LookupItem[]>("/lookups/categories?include_inactive=true").then(setCategories).catch(() => {});
    // TKT-00118: default the country to United States — the state and city
    // selects are gated on a country, so reaching them meant picking it by hand
    // on every visit.
    api.get<LookupItem[]>("/lookups/countries")
      .then((rows) => {
        setCountries(rows);
        const us = rows.find((c) => c.name === "United States");
        if (!us) return;
        defaultCountryIdRef.current = String(us.id);
        setFilterCountryId(String(us.id));
      })
      .catch(() => {})
      // Release the first fetch either way — a failed lookup must not leave the
      // page stuck on its loading state.
      .finally(() => setCountryReady(true));

    api.patch<{ ok: boolean; previous_last_seen_at: string | null }>("/business-listings/mark-listings-seen", {})
      .then((data) => setListingSeenAt(data.previous_last_seen_at))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!countryReady) return;
    fetchListings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryReady]);

  useEffect(() => {
    if (filterCountryId) {
      api.get<LookupItem[]>(`/lookups/states?country_id=${filterCountryId}`).then(setStates).catch(() => {});
    } else {
      setStates([]);
    }
    setFilterStateId("");
    setFilterCityId("");
    setCities([]);
  }, [filterCountryId]);

  useEffect(() => {
    if (filterStateId) {
      api.get<LookupItem[]>(`/lookups/cities?state_id=${filterStateId}`).then(setCities).catch(() => {});
    } else {
      setCities([]);
    }
    setFilterCityId("");
  }, [filterStateId]);

  async function fetchListings(targetPage = page, myOrgOverride?: boolean) {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("skip", String(targetPage * PAGE_SIZE));
      params.set("limit", String(PAGE_SIZE));
      if (searchQuery) params.set("search", searchQuery);
      if (filterStatusId) {
        params.set("listing_status_id", filterStatusId);
      } else {
        params.set("exclude_listing_status_id", "3");
      }
      const scopeToMyOrg = myOrgOverride ?? onlyMyOrg;
      if (scopeToMyOrg && user?.org_id) params.set("org_id", String(user.org_id));
      if (filterAgentId) params.set("agent_id", filterAgentId);
      if (filterCategoryId) params.set("industry_category_id", filterCategoryId);
      if (filterCountryId) params.set("country_id", filterCountryId);
      if (filterStateId) params.set("state_id", filterStateId);
      if (filterCityId) params.set("city_id", filterCityId);
      const data = await api.get<{ total_elements: number; total_pages: number; current_page: number; page_size: number; data: BusinessListing[] }>(`/admin/listings?${params.toString()}`);
      setListings(data.data);
      setTotal(data.total_elements);
    } catch {
      toast.error("Failed to load listings");
    } finally {
      setLoading(false);
    }
  }

  async function changeStatus(listingId: string, statusId: number, notes?: string) {
    setActionLoading(listingId);
    try {
      const payload: { listing_status_id: number; admin_notes?: string } = { listing_status_id: statusId };
      if (notes !== undefined) payload.admin_notes = notes;
      const updated = await api.patch<BusinessListing>(`/admin/listings/${listingId}/status`, payload);
      setListings((prev) => prev.map((listing) => (listing.id === listingId ? updated : listing)));
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to change status");
    } finally {
      setActionLoading(null);
      setStatusDropdown(null);
    }
  }

  async function deleteListing(listingId: string, title: string) {
    if (!confirm(`Delete \"${title}\"? You can restore it later from Deleted Listings.`)) return;

    setActionLoading(listingId);
    try {
      await api.delete(`/admin/listings/${listingId}`);
      setListings((prev) => prev.filter((listing) => listing.id !== listingId));
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to delete listing");
    } finally {
      setActionLoading(null);
    }
  }

  function submitNotes(listingId: string, currentStatusId: number) {
    changeStatus(listingId, currentStatusId, adminNotes);
    setNotesModal(null);
  }

  const handleSearch = (event: React.FormEvent) => {
    event.preventDefault();
    setPage(0);
    fetchListings(0);
  };

  // Clearing returns to the page baseline (United States), not to "All
  // countries"; that baseline doesn't count as an active filter. Admins who want
  // every country can still pick "All countries" in the dropdown.
  const clearFilters = () => {
    setSearchQuery("");
    setFilterStatusId("");
    setFilterAgentId("");
    setFilterCategoryId("");
    setFilterCountryId(defaultCountryIdRef.current);
    setFilterStateId("");
    setFilterCityId("");
    setPage(0);
    setTimeout(() => fetchListings(0), 0);
  };

  const toggleOrgScope = (next: boolean) => {
    setOnlyMyOrg(next);
    setPage(0);
    fetchListings(0, next);
  };

  const goToPage = (next: number) => {
    setPage(next);
    fetchListings(next);
  };

  // Passed as router state on each listing link so ListingDetail's
  // "Back to listings" returns here with the current page preserved.
  // Back to THIS queue (with its org prefix and page), not My Listings — the
  // two used to share /admin/listings.
  const { pathname, search } = useLocation();
  const backTo = `${pathname}${search}`;

  const hasFilters = Boolean(
    searchQuery ||
      filterStatusId !== "" ||
      filterAgentId ||
      filterCategoryId ||
      (filterCountryId && filterCountryId !== defaultCountryIdRef.current) ||
      filterStateId ||
      filterCityId
  );

  const metrics = useMemo(() => {
    const underReview = listings.filter((listing) => listing.listing_status_id === 2).length;
    const active = listings.filter((listing) => listing.listing_status_id === 3).length;
    const sold = listings.filter((listing) => listing.listing_status_id === 5).length;
    return { total, underReview, active, sold };
  }, [listings, total]);

  const formatShortPrice = (price: number | null) => {
    if (!price) return "-";
    if (price >= 1_000_000) return `$${(price / 1_000_000).toFixed(price % 1_000_000 === 0 ? 0 : 1)}m`;
    if (price >= 1_000) return `$${(price / 1_000).toFixed(price % 1_000 === 0 ? 0 : 1)}k`;
    return `$${price}`;
  };

  const getStatusLabel = (id: number) => LISTING_STATUSES.find((status) => status.id === id)?.label || `Status ${id}`;

  return (
    <PageShell>
      <PageHero
        eyebrow="Admin Listings"
        title="Moderate listing quality, status flow, and seller-facing outcomes."
        description="This moderation workspace is now structured for faster triage: filter precisely, open listing context instantly, and apply approvals or rejections without losing visibility."
        actions={
          <>
            <Button size="lg" className="rounded-full" onClick={() => fetchListings()}>
              Refresh moderation queue
              <ArrowRight className="h-4 w-4" />
            </Button>
            <Link to={orgScopedPath("/admin/home", user?.org_slug)}>
              <Button size="lg" variant="outline" className="rounded-full border-primary/20 bg-white/80 text-foreground hover:bg-white">
                Back to dashboard
              </Button>
            </Link>
          </>
        }
        aside={
          <>
            <div className="text-[0.72rem] font-bold uppercase tracking-[0.22em] text-primary/70">Current queue</div>
            <div className="mt-3 text-3xl font-black tracking-tight text-foreground">{metrics.total} listings</div>
            <p className="mt-2 text-sm leading-7 text-muted-foreground">
              {metrics.underReview} under review, {metrics.active} active, and {metrics.sold} sold in the current filtered view.
            </p>
            <div className="mt-5 grid grid-cols-2 gap-3">
              <div className="rounded-[1.3rem] border border-border/60 bg-white/70 p-4">
                <div className="text-[0.68rem] font-bold uppercase tracking-[0.18em] text-muted-foreground">Under Review</div>
                <div className="mt-2 text-2xl font-black tracking-tight text-foreground">{metrics.underReview}</div>
              </div>
              <div className="rounded-[1.3rem] border border-border/60 bg-white/70 p-4">
                <div className="text-[0.68rem] font-bold uppercase tracking-[0.18em] text-muted-foreground">Active</div>
                <div className="mt-2 text-2xl font-black tracking-tight text-foreground">{metrics.active}</div>
              </div>
            </div>
          </>
        }
      />

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <MetricTile label="Total Listings" value={metrics.total} icon={Search} tone="primary" />
        <MetricTile label="Under Review" value={metrics.underReview} icon={Clock} tone="amber" />
        <MetricTile label="Active" value={metrics.active} icon={CheckCircle2} tone="emerald" />
        <MetricTile label="Sold" value={metrics.sold} icon={DollarSign} tone="rose" />
      </section>

      <PagePanel>
        <PanelHeading
          title="Filter moderation view"
          description="Combine keyword, status, ownership, and location filters before taking moderation actions."
        />

        <form onSubmit={handleSearch} className="space-y-3">
          {/* Org admins can view every listing but only moderate their own
              organization's, so give them a one-click way back to the
              actionable subset instead of a queue that is mostly read-only. */}
          {isOrgAdmin(user) && (
            <div className="inline-flex items-center rounded-xl border border-border/80 bg-card/60 p-1">
              {[
                { label: "My organization", value: true },
                { label: "All listings", value: false },
              ].map((opt) => (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => toggleOrgScope(opt.value)}
                  aria-pressed={onlyMyOrg === opt.value}
                  className={cn(
                    "rounded-lg px-4 py-1.5 text-sm font-semibold transition-colors",
                    onlyMyOrg === opt.value
                      ? "bg-primary text-primary-foreground shadow"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
          <div className="grid gap-3 md:grid-cols-12 md:items-end">
            <div className="relative md:col-span-5">
              <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground/55" />
              <Input
                placeholder="Search by title or overview..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="pl-10"
              />
            </div>

            <div className="md:col-span-2 space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">Status</Label>
              <select
                value={filterStatusId}
                onChange={(event) => setFilterStatusId(event.target.value)}
                className="h-11 w-full rounded-2xl border border-border/80 bg-card/85 px-4 py-2 text-sm shadow-[0_1px_0_hsl(0_0%_100%/0.84)_inset] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25"
              >
                <option value="">All statuses</option>
                {LISTING_STATUSES.map((status) => (
                  <option key={status.id} value={status.id}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2 space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">BL Advisors</Label>
              <select
                value={filterAgentId}
                onChange={(event) => setFilterAgentId(event.target.value)}
                className="h-11 w-full rounded-2xl border border-border/80 bg-card/85 px-4 py-2 text-sm shadow-[0_1px_0_hsl(0_0%_100%/0.84)_inset] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25"
              >
                <option value="">All BL Advisors</option>
                {agents.map((agent) => (
                  <option key={agent.id} value={agent.id}>
                    {agent.first_name} {agent.last_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-3">
              <Button type="submit" className="w-full rounded-full">
                <Search className="h-4 w-4" />
                Search listings
              </Button>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-12 md:items-end">
            <div className="md:col-span-3 space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">Category</Label>
              <select
                value={filterCategoryId}
                onChange={(event) => setFilterCategoryId(event.target.value)}
                className="h-11 w-full rounded-2xl border border-border/80 bg-card/85 px-4 py-2 text-sm shadow-[0_1px_0_hsl(0_0%_100%/0.84)_inset] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25"
              >
                <option value="">All categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-3 space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">Country</Label>
              <select
                value={filterCountryId}
                onChange={(event) => setFilterCountryId(event.target.value)}
                className="h-11 w-full rounded-2xl border border-border/80 bg-card/85 px-4 py-2 text-sm shadow-[0_1px_0_hsl(0_0%_100%/0.84)_inset] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25"
              >
                <option value="">All countries</option>
                {countries.map((country) => (
                  <option key={country.id} value={country.id}>
                    {country.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2 space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">State</Label>
              <select
                value={filterStateId}
                onChange={(event) => setFilterStateId(event.target.value)}
                disabled={!filterCountryId}
                className="h-11 w-full rounded-2xl border border-border/80 bg-card/85 px-4 py-2 text-sm shadow-[0_1px_0_hsl(0_0%_100%/0.84)_inset] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25 disabled:opacity-50"
              >
                <option value="">All states</option>
                {states.map((state) => (
                  <option key={state.id} value={state.id}>
                    {state.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2 space-y-1.5">
              <Label className="text-xs font-bold uppercase tracking-[0.16em] text-muted-foreground">City</Label>
              <select
                value={filterCityId}
                onChange={(event) => setFilterCityId(event.target.value)}
                disabled={!filterStateId}
                className="h-11 w-full rounded-2xl border border-border/80 bg-card/85 px-4 py-2 text-sm shadow-[0_1px_0_hsl(0_0%_100%/0.84)_inset] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/25 disabled:opacity-50"
              >
                <option value="">All cities</option>
                {cities.map((city) => (
                  <option key={city.id} value={city.id}>
                    {city.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              {hasFilters && (
                <button
                  type="button"
                  onClick={clearFilters}
                  className="inline-flex h-11 w-full items-center justify-center rounded-full bg-destructive/10 px-3 text-xs font-bold uppercase tracking-[0.15em] text-destructive transition-colors duration-200 hover:bg-destructive/15"
                >
                  Clear filters
                </button>
              )}
            </div>
          </div>
        </form>
      </PagePanel>

      {notesModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <Card className="w-full max-w-lg shadow-2xl">
            <CardContent className="space-y-4 p-6">
              <h3 className="text-xl font-black tracking-tight text-foreground">Admin notes</h3>
              <p className="text-sm leading-7 text-muted-foreground">
                Add moderation context for this listing. Notes are only visible within admin workflows.
              </p>
              <Textarea
                rows={5}
                placeholder="Add notes about listing quality, missing details, or decision rationale..."
                value={adminNotes}
                onChange={(event) => setAdminNotes(event.target.value)}
              />
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setNotesModal(null)}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    const listing = listings.find((item) => item.id === notesModal);
                    if (listing) submitNotes(notesModal, listing.listing_status_id);
                  }}
                >
                  Save notes
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <PagePanel>
        <div className="flex items-center justify-between">
          <PanelHeading
            title="Moderation list"
            description="Open each listing quickly, update status, annotate decisions, and remove invalid inventory."
          />
          <div className="flex items-center gap-1 rounded-xl border border-border/80 bg-card p-1 self-start sm:self-auto">
            <button
              onClick={() => setViewMode("list")}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                viewMode === "list"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              )}
              title="List view"
            >
              <LayoutGrid className="h-4 w-4" />
              <span className="hidden sm:inline">Grid</span>
            </button>
            <button
              onClick={() => setViewMode("table")}
              className={cn(
                "inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                viewMode === "table"
                  ? "bg-primary text-primary-foreground shadow-sm"
                  : "text-muted-foreground hover:bg-accent hover:text-foreground"
              )}
              title="Table view"
            >
              <List className="h-4 w-4" />
              <span className="hidden sm:inline">Table</span>
            </button>
          </div>
        </div>

        {loading ? (
          <Loader className="min-h-[26vh]" />
        ) : listings.length === 0 ? (
          <EmptyPanel
            icon={Search}
            title="No listings found"
            description="Try broadening your filters or refresh moderation data to load more inventory."
          />
        ) : viewMode === "table" ? (
          <div className="overflow-x-auto rounded-2xl border border-border/60">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60 bg-muted/40 text-left text-[0.7rem] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                  <th className="px-4 py-3">Title</th>
                  <th className="px-4 py-3">Seller</th>
                  <th className="px-4 py-3">Price</th>
                  <th className="px-4 py-3">Badges</th>
                  <th className="px-4 py-3">Listed</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {listings.map((listing) => {
                  const isActing = actionLoading === listing.id;
                  const isNew = Boolean(
                    listing.created_at &&
                      (!listingSeenAt || new Date(listing.created_at) > new Date(listingSeenAt))
                  );
                  return (
                    <tr
                      key={listing.id}
                      className={`border-b border-border/40 bg-white transition-colors duration-150 last:border-0 hover:bg-accent/30 ${
                        isActing ? "pointer-events-none opacity-50" : ""
                      }`}
                    >
                      <td className="max-w-[220px] px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          <Link
                            to={`/listings/${listing.id}`}
                            state={{ from: backTo }}
                            className="truncate font-semibold text-foreground hover:text-primary"
                          >
                            {listing.title}
                          </Link>
                          <Link to={`/listings/${listing.id}`} state={{ from: backTo }} className="shrink-0 text-muted-foreground hover:text-primary">
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                          <ListingHotBadge published_at={listing.published_at} size="sm" />
                          {isNew && !listing.created_at && (
                            <span className="shrink-0 rounded-full bg-gold-gradient px-2 py-0.5 text-[0.6rem] font-bold uppercase tracking-[0.14em] text-primary-foreground">
                              New
                            </span>
                          )}
                        </div>
                        <div className="mt-0.5 font-mono text-[0.68rem] text-muted-foreground">{listing.reference_code ?? `${listing.id.slice(0, 8)}...`}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="font-medium text-foreground">{listing.seller_name || listing.seller_id.slice(0, 8)}</div>
                        {listing.seller_email && (
                          <div className="text-[0.68rem] text-muted-foreground">{listing.seller_email}</div>
                        )}
                        {listing.agent_name && (
                          <div className="mt-0.5 text-[0.68rem] text-primary">Advisor: {listing.agent_name}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 font-semibold text-emerald-600">{formatShortPrice(listing.asking_price)}</td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {listing.is_sba_approved && (
                            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-[0.1em] text-amber-600">
                              SBA
                            </span>
                          )}
                          {listing.seller_financing && (
                            <span className="rounded-full bg-green-500/15 px-2 py-0.5 text-[0.65rem] font-bold uppercase tracking-[0.1em] text-green-600">
                              Seller Fin.
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {new Date(listing.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[0.68rem] font-bold uppercase tracking-[0.12em] ${
                          STATUS_COLORS[listing.listing_status_id] || "bg-slate-100 text-slate-700"
                        }`}>
                          {STATUS_ICONS[listing.listing_status_id]}
                          {getStatusLabel(listing.listing_status_id)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {listing.listing_status_id === 2 && (
                            <>
                              <Button
                                size="sm"
                                onClick={() => changeStatus(listing.id, 3)}
                                disabled={!canActOnListingOrg(user, listing.org_id)}
                                className="h-7 gap-1 rounded-full bg-emerald-600 px-2.5 text-[0.65rem] font-bold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
                                title={canActOnListingOrg(user, listing.org_id) ? "Approve listing" : "Managed by another organization"}
                              >
                                <CheckCircle2 className="h-3 w-3" />
                                Approve
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => changeStatus(listing.id, 8)}
                                disabled={!canActOnListingOrg(user, listing.org_id)}
                                className="h-7 gap-1 rounded-full bg-rose-600 px-2.5 text-[0.65rem] font-bold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-40"
                                title={canActOnListingOrg(user, listing.org_id) ? "Reject listing" : "Managed by another organization"}
                              >
                                <XCircle className="h-3 w-3" />
                                Reject
                              </Button>
                            </>
                          )}
                          <Link to={`/listings/${listing.id}`} state={{ from: backTo }}>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-primary" title="View listing">
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                          </Link>
                          {canActOnListingOrg(user, listing.org_id) && (
                            <>
                              <Link to={`/listings/${listing.id}/edit`}>
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-muted-foreground hover:text-foreground" title="Edit listing">
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                              </Link>
                              <Button variant="ghost" size="icon" onClick={() => deleteListing(listing.id, listing.title)} className="h-7 w-7 text-destructive hover:bg-destructive/10" title="Delete listing">
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="space-y-3">
            {listings.map((listing) => {
              const isActing = actionLoading === listing.id;
              const isNew = Boolean(
                listing.created_at &&
                  (!listingSeenAt || new Date(listing.created_at) > new Date(listingSeenAt))
              );

              return (
                <article
                  key={listing.id}
                  className={`rounded-[1.55rem] border border-border/60 bg-white/72 p-4 shadow-[0_1px_0_hsl(0_0%_100%/0.82)_inset] transition-all duration-200 ${
                    isActing ? "opacity-55 pointer-events-none" : "hover:-translate-y-0.5 hover:border-primary/20 hover:bg-white"
                  }`}
                >
                  <div className="flex flex-col gap-3 xl:flex-row xl:items-start">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start gap-2">
                        <Link
                          to={`/listings/${listing.id}`}
                          state={{ from: backTo }}
                          className="truncate font-display text-xl font-bold leading-snug text-foreground transition-colors duration-200 hover:text-primary"
                        >
                          {listing.title}
                        </Link>
                        <Link to={`/listings/${listing.id}`} state={{ from: backTo }} className="mt-0.5 text-muted-foreground hover:text-primary">
                          <ExternalLink className="h-3.5 w-3.5" />
                        </Link>
                        <ListingHotBadge published_at={listing.published_at} size="md" />
                        {isNew && !listing.created_at && (
                          <span className="badge-new-flash rounded-full bg-gold-gradient px-2.5 py-1 text-[0.62rem] font-bold uppercase tracking-[0.15em] text-primary-foreground">
                            New
                          </span>
                        )}
                      </div>

                      {listing.reference_code && (
                        <p className="mt-0.5 font-mono text-[0.65rem] tracking-wide text-muted-foreground">{listing.reference_code}</p>
                      )}

                      {listing.overview && (
                        <p className="mt-2 line-clamp-2 text-sm leading-7 text-muted-foreground">
                          {listing.overview}
                        </p>
                      )}

                      <div className="mt-3 flex flex-wrap gap-2 text-xs">
                        <span className="rounded-full bg-primary/10 px-3 py-1.5 font-bold uppercase tracking-[0.14em] text-primary">
                          {formatShortPrice(listing.asking_price)}
                        </span>
                        {listing.revenue != null && (
                          <span className="rounded-full bg-primary/10 px-3 py-1.5 font-bold uppercase tracking-[0.14em] text-primary">
                            Rev {formatShortPrice(listing.revenue)}
                          </span>
                        )}
                        {listing.ebitda != null && (
                          <span className="rounded-full bg-primary/10 px-3 py-1.5 font-bold uppercase tracking-[0.14em] text-primary">
                            EBITDA {formatShortPrice(listing.ebitda)}
                          </span>
                        )}
                        <span className="rounded-full bg-slate-100 px-3 py-1.5 font-bold uppercase tracking-[0.14em] text-slate-700">
                          Seller {listing.seller_name || `${listing.seller_id.slice(0, 8)}...`}
                        </span>
                        {listing.agent_id && (
                          <span className="rounded-full bg-primary/10 px-3 py-1.5 font-bold uppercase tracking-[0.14em] text-primary">
                            BL Advisor {listing.agent_name || `${listing.agent_id.slice(0, 8)}...`}
                          </span>
                        )}
                        {listing.is_sba_approved && (
                          <span className="rounded-full bg-amber-500/15 px-3 py-1.5 font-bold uppercase tracking-[0.14em] text-amber-600">
                            SBA approved
                          </span>
                        )}
                        {listing.seller_financing && (
                          <span className="rounded-full bg-green-500/15 px-3 py-1.5 font-bold uppercase tracking-[0.14em] text-green-600">
                            Seller Financing
                          </span>
                        )}
                      </div>

                      <div className="mt-3 flex flex-wrap gap-3 text-xs text-muted-foreground">
                        <span>Listed: {new Date(listing.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-2 xl:justify-end">
                      <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-[0.14em] ${STATUS_COLORS[listing.listing_status_id] || "bg-slate-100 text-slate-700"}`}>
                        {STATUS_ICONS[listing.listing_status_id]}
                        {getStatusLabel(listing.listing_status_id)}
                      </span>

                      {/* Same organization gate as the table view above: an
                          org admin sees every listing here but may only act on
                          its own. The API returns 403 either way — this keeps
                          the button from promising something it can't do. */}
                      {listing.listing_status_id === 2 && (
                        <>
                          <Button
                            size="sm"
                            onClick={() => changeStatus(listing.id, 3)}
                            disabled={!canActOnListingOrg(user, listing.org_id)}
                            className="gap-1.5 rounded-full bg-emerald-600 px-3 text-xs font-bold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
                            title={canActOnListingOrg(user, listing.org_id) ? "Approve listing" : "Managed by another organization"}
                          >
                            <CheckCircle2 className="h-3.5 w-3.5" />
                            Approve
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => changeStatus(listing.id, 8)}
                            disabled={!canActOnListingOrg(user, listing.org_id)}
                            className="gap-1.5 rounded-full bg-rose-600 px-3 text-xs font-bold text-white hover:bg-rose-700 disabled:cursor-not-allowed disabled:opacity-40"
                            title={canActOnListingOrg(user, listing.org_id) ? "Reject listing" : "Managed by another organization"}
                          >
                            <XCircle className="h-3.5 w-3.5" />
                            Reject
                          </Button>
                        </>
                      )}

                      <Link to={`/listings/${listing.id}`} state={{ from: backTo }}>
                        <Button variant="ghost" size="icon" title="View listing" className="text-muted-foreground hover:text-primary">
                          <Eye className="h-4 w-4" />
                        </Button>
                      </Link>
                      {canActOnListingOrg(user, listing.org_id) && (
                        <>
                          <Link to={`/listings/${listing.id}/edit`}>
                            <Button variant="ghost" size="icon" title="Edit listing" className="text-muted-foreground hover:text-foreground">
                              <Pencil className="h-4 w-4" />
                            </Button>
                          </Link>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => deleteListing(listing.id, listing.title)}
                            className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}

        {!loading && total > 0 && (
          <Pagination
            page={page + 1}
            totalElements={total}
            pageSize={PAGE_SIZE}
            onChange={(p) => goToPage(p - 1)}
          />
        )}
      </PagePanel>
    </PageShell>
  );
}
